import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import {
  PaginationType,
  UseMutationProps,
  UseQueryProps,
} from "@lib/types/Api";
import { Button, Form, Input, Modal, Popconfirm, Space } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import useIntl from "react-intl/lib/src/components/useIntl";
import { noteSchema as yupSchema } from "@lib/schemas/note";
import { createYupSync } from "@lib/helpers/TableHelper";
import { FormattedMessage } from "react-intl";
import { Customer } from "@lib/types/Customer";
import { Note } from "@lib/types/Note";
import { Invoice, Product } from "@lib/types/Billing";
import NoteInvoiceInput from "./NoteFormInputs/NoteInvoiceInput";
import NoteProductInput from "./NoteFormInputs/NoteProductInput";
import NoteCustomerInput from "./NoteFormInputs/NoteCustomerInput";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props<T> = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  query?: UseQueryProps<PaginationType<T>>;
  mutationQuery?: UseMutationProps<Partial<T>>;
  deleteQuery?: UseMutationProps<Partial<T>>;
  selected?: T;
  product?: Product;
  invoice?: Invoice;
  customer?: Customer;
};

export default function NotesModal<T extends Note>({
  openModal,
  toggleModal,
  query,
  mutationQuery,
  deleteQuery,
  selected,
  invoice,
  product,
  customer,
}: Props<T>) {
  const { messages } = useIntl();
  const [form] = Form.useForm();

  const { mutate, isPending } = mutationQuery;

  const initialValues = useMemo(() => {
    return selected
      ? selected
      : ({
          text: "",
          customerId: customer?.id || null,
          invoiceId: invoice?.id || null,
          productId: product?.id || null,
        } as Partial<T>);
  }, [selected]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, [initialValues, openModal]);

  const handleClose = useCallback(() => {
    handleResetForm();
    toggleModal(false);
  }, [toggleModal]);

  useEffect(() => {
    if (!openModal) return;

    handleResetForm();
  }, [openModal]);

  const isInvoiceNote = !!invoice;
  const isProductNote = !!product;

  return (
    <Modal
      title={
        <FormattedMessage
          id={selected ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.note"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      <Form<T>
        {...layout}
        form={form}
        name={"product-modal"}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={initialValues}
        onFinish={(values) =>
          mutate({
            ...values,
            customerId: customer?.id,
            invoiceId: invoice?.id,
            productId: product?.id,
            id: selected?.id,
          })
        }
      >
        {isInvoiceNote && (
          <NoteInvoiceInput
            form={form}
            loading={query?.isLoading || mutationQuery.isPending}
            note={selected}
            invoice={invoice}
          />
        )}

        {isProductNote && (
          <NoteProductInput
            form={form}
            loading={query?.isLoading || mutationQuery.isPending}
            note={selected}
            product={product}
          />
        )}

        <NoteCustomerInput
          form={form}
          loading={query?.isLoading || mutationQuery.isPending}
          note={selected}
          customer={customer}
        />

        <Form.Item
          name="text"
          label={messages["common.note"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input.TextArea />
        </Form.Item>

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            {deleteQuery && (
              <Popconfirm
                title={
                  <FormattedMessage
                    id={"common.confirm.delete.title"}
                    values={{ name: messages["common.note"] as string }}
                  />
                }
                description={
                  <FormattedMessage
                    id={"common.confirm.delete.message"}
                    values={{ name: messages["common.note"] as string }}
                  />
                }
                onConfirm={() => {
                  deleteQuery?.mutate(selected);
                }}
                okText={messages["common.yes"] as string}
                cancelText={messages["common.no"] as string}
              >
                <Button color="red" variant="solid" disabled={isPending}>
                  {messages["common.delete"] as string}
                </Button>
              </Popconfirm>
            )}
            <Button
              htmlType="button"
              onClick={handleResetForm}
              disabled={isPending}
            >
              {messages["common.reset"] as string}
            </Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              {selected?.id
                ? (messages["common.update"] as string)
                : (messages["common.create"] as string)}
            </Button>
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
